import React, { createContext, useEffect, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useLocation, useSearchParams } from 'react-router-dom';
import UserProfileDTO from 'src/models/generated/UserProfileDTO';
import AuthProfileDTO from 'src/models/generated/AuthProfileDTO';
import { useStateAsync } from 'src/hooks/UseStateAsync';
import ApiController from 'src/api/ApiController';
import { useLocalStorage } from 'src/hooks';
import { SetAccessToken } from 'src/config/ApiConfig';

export interface AuthenticationContextType {
    /** Just for you, so you can check if the user is logged in easily */
    isLoggedIn: boolean;
    /** Indicates that the authContext has finished determining the current state and is ready to use */
    isInitialized: boolean;
    /** Used for reporting and the debug overlay */
    loginTimeStamp: moment.Moment | null;

    accountNumber: string;
    licenseNumber: string;
    password: string;

    // Needed when the user updates their profile info
    updateAccountNumber: (accountNumber: string) => Promise<void>;
    updateLicenseNumber: (licenseNumber: string) => Promise<void>;
    updatePassword: (password: string) => Promise<void>;

    // Async retrieval for the profile and auth objects
    getAccountNumber: () => Promise<string>;
    getLicenseNumber: () => Promise<string>;
    getPassword: () => Promise<string>;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const AuthenticationContext = createContext<AuthenticationContextType>(undefined!);

export const AuthenticationProvider = (props: React.PropsWithChildren<any>) => {
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [isInitialized, setIsInitialized] = useState(true);
    const [loginTimeStamp, setLoginTimeStamp] = useState<moment.Moment | null>(null);
    const [accountNumber, setAccountNumber, getAccountNumberAsync] = useStateAsync('');
    const [licenseNumber, setLicenseNumber, getLicenseNumberAsync] = useStateAsync('');
    const [password, setPassword, getPasswordAsync] = useStateAsync('');
    const [cachedPassword, setCachedPassword, getCachedPasswordAsync] = useLocalStorage('cachedPassword');
    const [cachedPasswordLastUsed, setCachedPasswordLastUsed, getCachedPasswordLastUsedAsync] = useLocalStorage('cachedPasswordLastUsed');

    useEffect(() => {
        loadPassword();
    }, []);

    const loadPassword = async () => {
        const password = await getCachedPasswordAsync();
        const lastUsed = await getCachedPasswordLastUsedAsync();
        const lastUsedDate = lastUsed ? new Date(lastUsed) : new Date();
        if (lastUsedDate != null && moment().isSame(moment(lastUsedDate), 'day')) {
            updatePassword(password || '');
        }
    };

    const unloadComponent = () => {
        setIsLoggedIn(false);
        setIsInitialized(true); // Kind of strange but since this is normally called on logout, it makes sense. We are ready, there is no user!
        setLoginTimeStamp(null);
        setAccountNumber('');
        setLicenseNumber('');
    };

    const updateAccountNumber = (newAccountNumber: string) => {
        return new Promise<void>(resolve => {
            setAccountNumber(newAccountNumber);
            resolve();
        });
    };

    const updateLicenseNumber = (newLicenseNumber: string) => {
        return new Promise<void>(resolve => {
            setLicenseNumber(newLicenseNumber);
            resolve();
        });
    };

    const updatePassword = (newPassword: string) => {
        return new Promise<void>(resolve => {
            setPassword(newPassword);
            setCachedPassword(newPassword);
            setCachedPasswordLastUsed(moment().format('LLL'));
            SetAccessToken(newPassword);
            resolve();
        });
    };

    return (
        <AuthenticationContext.Provider value={{
            isLoggedIn,
            isInitialized,
            loginTimeStamp,
            accountNumber,
            licenseNumber,
            password,
            updateAccountNumber,
            updateLicenseNumber,
            updatePassword,
            getAccountNumber: getAccountNumberAsync,
            getLicenseNumber: getLicenseNumberAsync,
            getPassword: getPasswordAsync,
        }}>
            {props.children}
        </AuthenticationContext.Provider>
    );
};
